/**
 * Formato de puntuaciones.
 *
 * Espejo de format_score() / format_duration() en 0002_scoring.sql.
 * Las cadenas DEBEN coincidir carater por caracter con las de Postgres:
 * el leaderboard mezcla valores renderizados en el servidor (snapshot
 * cacheado) con valores calculados en el cliente (captura optimista del
 * juez), y una diferencia de formato se ve como un parpadeo.
 */
/** Segundos -> "m:ss". Sin horas: en CrossFit 62:30 se lee sin ambiguedad. */
export function formatDuration(seconds) {
    if (seconds == null || Number.isNaN(seconds))
        return null;
    const minutes = Math.floor(seconds / 60);
    const secs = Math.floor(seconds) % 60;
    return `${minutes}:${String(secs).padStart(2, '0')}`;
}
function trimNumber(value) {
    return String(Math.round(value * 10) / 10);
}
export function formatScore(workout, score) {
    switch (score.status) {
        case 'dnf':
            return 'DNF';
        case 'dq':
            return 'DQ';
        case 'dns':
        case 'withdrawn':
            return '--';
        case 'pending':
            return '...';
    }
    switch (workout.scoreType) {
        case 'time':
            if (score.capped) {
                // Convencion estandar: CAP+N = llego al time cap con N reps sin hacer.
                const remaining = Math.max((workout.repTarget ?? 0) - (score.reps ?? 0), 0);
                return `CAP+${remaining}`;
            }
            return formatDuration(score.timeSeconds);
        case 'reps':
            return `${score.reps ?? 0} reps`;
        case 'load':
            return `${trimNumber(score.loadKg ?? 0)} kg`;
        case 'distance':
            return `${trimNumber(score.distanceM ?? 0)} m`;
    }
}
/**
 * Convierte lo que teclea un juez en segundos.
 * Acepta "4:32", "12:05.4", "1:02:30" y segundos sueltos ("272").
 * Devuelve null si no es interpretable -- nunca un numero inventado.
 */
export function parseTimeInput(input) {
    const raw = input.trim();
    if (!raw)
        return null;
    const parts = raw.split(':');
    if (parts.length > 3)
        return null;
    const numbers = [];
    for (const part of parts) {
        if (!/^\d+(\.\d+)?$/.test(part.trim()))
            return null;
        numbers.push(Number(part));
    }
    // Los campos que no son el mas significativo no pueden pasar de 59.
    for (let i = 1; i < numbers.length; i++) {
        if (numbers[i] >= 60)
            return null;
    }
    if (numbers.length === 1)
        return numbers[0];
    if (numbers.length === 2)
        return numbers[0] * 60 + numbers[1];
    return numbers[0] * 3600 + numbers[1] * 60 + numbers[2];
}
/** Ordinal en espanol para el leaderboard: 1.o, 2.o, 3.er ... */
export function formatPlace(place) {
    return `${place}.º`;
}
/**
 * Tiempo con centesimas, para capturar la marca de un atleta.
 *
 * `formatDuration` corta con floor porque sirve para time caps y para el
 * leaderboard, donde "12:00" es un dato redondo y ".00" solo estorba.
 * Aqui es al reves: es la marca de una persona.
 *
 * Centesimas y no milesimas a proposito. El tiempo se toma cuando el juez
 * toca el carril, y en ese toque hay dos o tres decimas de reaccion
 * humana. Escribir milesimas seria inventar una precision que no existe y
 * dar pie a discutir un desempate que en realidad mide quien tiene el
 * dedo mas rapido.
 */
export function formatDurationPrecise(seconds) {
    if (seconds == null || Number.isNaN(seconds))
        return null;
    // Todo en centesimas enteras. Restar la parte entera de un float da
    // 0.4499999... para 503.45, y truncar eso convierte 8:23.45 en 8:23.44:
    // un tiempo de competencia mal escrito por un error de coma flotante.
    const cs = Math.round(Math.max(0, seconds) * 100);
    const minutes = Math.floor(cs / 6000);
    const secs = Math.floor((cs % 6000) / 100);
    const cent = cs % 100;
    return `${minutes}:${String(secs).padStart(2, '0')}.${String(cent).padStart(2, '0')}`;
}
/**
 * Da forma de reloj a lo que se va teclando.
 *
 * Los digitos entran por la derecha, como en un cronometro: 1 -> 0:01,
 * 12 -> 0:12, 120 -> 1:20, 1200 -> 12:00. Asi el organizador teclea
 * cuatro numeros del teclado numerico y no pelea con los dos puntos, que
 * en un movil es justo la tecla escondida.
 *
 * Devuelve la cadena ya formateada; no valida nada. Para pasar a segundos
 * esta `parseTimeInput`.
 */
export function formatTimeDigits(input) {
    const digitos = input.replace(/\D/g, '').replace(/^0+(?=\d)/, '').slice(-6);
    if (digitos === '')
        return '';
    const n = digitos.padStart(3, '0');
    const seg = n.slice(-2);
    const resto = n.slice(0, -2);
    // Mas de 59 minutos pasa a h:mm:ss, que es lo que espera parseTimeInput.
    if (resto.length > 2) {
        const min = resto.slice(-2);
        return `${resto.slice(0, -2)}:${min}:${seg}`;
    }
    return `${String(Number(resto))}:${seg}`;
}
//# sourceMappingURL=format.js.map